<script setup>
import api from "@/api";
import { useInteractionTrainingStore } from "@/stores/interactionTraining";
import { useOrganizationStore } from "@/stores/organization";
import { useThreadsContextStore } from "@/stores/threads";
import { useUserStore } from "@/stores/user";
import { useWfbChatWidgetStore } from "@/stores/wfbChatWidget";
import {  ThreadStatusEnum } from "@/utils/constants";
import { useToast } from "vue-toastification";
import ThreadStatusBadge2 from "./ThreadStatusBadge2.vue";

const props = defineProps({
  threadId: { type: String, required: true },
});

const toast = useToast()
const threadsStore = useThreadsContextStore();
const interactionTrainingStore = useInteractionTrainingStore();
const organizationStore = useOrganizationStore();
const userStore = useUserStore();
const wfbChatWidgetStore = useWfbChatWidgetStore();
const readOnlyMode = inject("READ_ONLY_MODE")
const liveDialects = ref(null);

const thread = computed({
  get: () => threadsStore.getThreadById(props.threadId),
  set: () => threadsStore.patchThread(props.threadId)
})

const organizationId = computed(() => userStore.authUserOrganizationId);
const canUseDialectTraining = computed(
  () =>
    !readOnlyMode.value &&
    userStore.canCreateDialects &&
    organizationStore.myOrgEnablements.includes("agentic.adoption"),
);
const hasExistingTraining = computed(() => Boolean(liveDialects.value?.length));
const dialectSessionKey = computed(() => `dialect_builder.thread.${props.threadId}`);

/** Load live trainings so the thread CTA can show whether one already exists. */
const loadLiveDialects = async () => {
  liveDialects.value = null;
  if (!canUseDialectTraining.value || !organizationId.value) return;

  const threadId = props.threadId;
  const orgId = organizationId.value;
  try {
    const response = await api.dialects.fetchLiveDialectsByThread(threadId, orgId);
    if (threadId !== props.threadId || orgId !== organizationId.value) return;

    liveDialects.value = response.data;
  } catch (error) {
    console.error("Error checking for existing AI training:", error);
  }
};

/** Open the thread-scoped AI training chat and close the interaction training popup. */
const openDialect = () => {
  const trainingTablePath = `/dialects?view=live&source_thread_id=${props.threadId}`;
  const isGlobalManager = userStore.dialectCapability === "global_and_personal";
  const historyHint = liveDialects.value.length === 1 || isGlobalManager
    ? " Click View History to see the training chat history."
    : "";
  const extras = {
    mode: "dialect_builder",
    source: "platform_thread_detail",
    thread_id: props.threadId,
    org_id: organizationId.value,
  };
  const dialect = liveDialects.value.length === 1 ? liveDialects.value[0] : null;
  if (dialect) {
    extras.dialect_id = dialect.id;
    extras.expected_version = dialect.version;
  }

  let initialMessage =
    "Looks like you want to create an AI training based on this thread. How can I help?";
  if (dialect) {
    const trainingId = dialect.display_id ? `T${dialect.display_id}` : "AI training";
    const trainingLink = `[${trainingId}](${trainingTablePath})`;
    initialMessage = `This thread already has an active AI training: ${trainingLink}. You can use this chat to update it.${historyHint}`;
  } else if (hasExistingTraining.value) {
    initialMessage = `This thread already has active AI trainings. [View them in the AI Trainings table](${trainingTablePath}) before creating another one.${historyHint}`;
  }

  interactionTrainingStore.resetActiveInteraction();
  return wfbChatWidgetStore.open({
    sessionKey: dialectSessionKey.value,
    chatOrigin: "dialect_builder",
    title: hasExistingTraining.value ? "Existing AI Training" : "Create AI Training",
    extras,
    historyThreadId: hasExistingTraining.value && isGlobalManager ? props.threadId : null,
    loadHistory: false,
    initialMessage,
  });
};

// Refresh when the visible thread or the user's access changes.
watch(
  [() => props.threadId, organizationId, canUseDialectTraining],
  loadLiveDialects,
  { immediate: true },
);

// The chat may create or update a training, so refresh its CTA when it closes.
watch(
  () => wfbChatWidgetStore.isWidgetOpen,
  (isOpen) => {
    if (!isOpen && wfbChatWidgetStore.sessionKey === dialectSessionKey.value) {
      loadLiveDialects();
    }
  },
);

const showReopenTicketButton = computed(() => {
  const validStatuses = [
    ThreadStatusEnum.SUPPORT_CLOSED,
    ThreadStatusEnum.USER_CLOSED,
    ThreadStatusEnum.AUTO_CLOSED,
  ]
  return thread.value && validStatuses.includes(thread.value.status)
})

const showConvertToTicketButton = computed(() => {
  const validStatuses = [
  ThreadStatusEnum.SOFT_ALERT,
  ThreadStatusEnum.AI_CLOSED
  ]
  return thread.value && validStatuses.includes(thread.value.status)
})

const showGoodAiReplyButton = computed(() => thread.value && thread.value.status === ThreadStatusEnum.SOFT_ALERT)

const reopenTicket = async () => {
  const payload = { status: ThreadStatusEnum.REOPENED };
  try {
    await threadsStore.patchThread(props.threadId, payload);
    toast.success("Ticket status successfully updated!");
  } catch (err) {
    toast.error("Failed to update ticket status");
  }
};

const convertToAiClosed = async () => {
  const payload = { status: ThreadStatusEnum.AI_CLOSED };
  try {
    await threadsStore.patchThread(props.threadId, payload);
    toast.success("Ticket status successfully updated!");
  } catch (err) {
    toast.error("Failed to update ticket status");
  }
};

const convertToTicket = async () => {
  try {
    await threadsStore.patchToTicket(props.threadId);
    toast.success("Ticket status successfully updated!");
  } catch (err) {
    toast.error("Failed to update ticket status");
  }
};
</script>

<template>
  <div class="banner">
    <div class="d-flex justify-content-between align-items-center">
      <div class="d-flex align-items-center gap-3">
      <span class="text-neutral-350 font-size-xs">
      #{{ thread?.short_id || 0 }}
    </span>
      <ThreadStatusBadge2
        v-if="!readOnlyMode"
        class="px-4 py-2 fw-semibold mx-0 pe-none"
        :key="thread.status"
        :value="thread.status"
        :selected="false"
      />
      <EndUserStatusBadge 
        v-else
        class="px-4 py-2 fw-semibold mx-0 pe-none"
        :value="thread.status"
        :selected="false"
      />
    </div>
      <div id="banner-buttons" class="d-flex justify-content-between">
        <div
          v-if="!readOnlyMode"
          class="buttons-container"
          :class="{ 'soft-alert-buttons': showGoodAiReplyButton }"
        >
          <DialectCTALabel
            v-if="canUseDialectTraining && liveDialects"
            class="ai-training-button"
            :label="hasExistingTraining ? 'Existing AI Training' : 'Initiate AI Training'"
            @onClick="openDialect"
          />
          <CustomButton
            v-if="showConvertToTicketButton"
            buttonType="blue-transparent"
            @click.stop="convertToTicket"
          >  
            <span>Convert to Ticket</span>
          </CustomButton>
          <CustomButton
            v-if="showReopenTicketButton"
            buttonType="blue-transparent"
            @click.stop="reopenTicket"
          >
            <span>Reopen Ticket</span>
          </CustomButton>
          <CustomButton 
            v-if="showGoodAiReplyButton"
            buttonType="blue"
            @click.stop="convertToAiClosed"
          >
            <span>Good Ai Response</span>
          </CustomButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.banner {
  position: sticky;
  top: 0;
  z-index: 30;
  padding: 1rem;
  background-color: white !important;
  box-shadow: 0 4px 6px rgba(5, 3, 3, 0.15);
  border-bottom: 0.25px solid $neutral-100;
  border-top: 0.25px solid $neutral-100;

  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 1rem;

  .buttons-container {
    display: flex;
    justify-content: center;
    gap: 0.5rem;

    &.soft-alert-buttons {
      display: grid;
      grid-template-columns: repeat(2, auto);

      .ai-training-button {
        grid-column: 1 / -1;
        grid-row: 2;
        justify-self: center;
      }
    }
  }
}
</style>
